import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import ExperienceWalletForm from 'components/forms/experienceWallet/ExperienceWalletForm';
import OnboardingSteps from 'components/OnboardingSteps';
import OnboardingNavigationButtons from 'components/OnboardingNavigationButtons';
import styles from 'styles/login.module.scss';
import layoutStyles from 'styles/layout.module.scss';
import { trackEvent } from 'lib/analytics';
import { EMPTY_EXPERIENCE } from 'constants/experienceDefaults';
import { WORK_ITEMS } from 'constants/experienceTypeOptions';
import OnboardingContentWrapper from 'components/OnboardingContentWrapper';
import { useQueryClient } from '@tanstack/react-query';
import { apiRequest } from 'lib/API';

const WORK_TYPES = WORK_ITEMS.map((item) => item.key);

export default function OnboardingEducation() {
  const intl = useIntl();
  const history = useHistory();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const queryClient = useQueryClient();

  // Read cached experience data for pre-population on back navigation
  const cachedExp = queryClient.getQueryData(['/users/me/experience/']);
  const hasMilitaryService =
    cachedExp != null && (cachedExp.current_military_service === true || cachedExp.previous_military_service === true);
  const cachedEdu = cachedExp?.job_experience?.find((e) => !WORK_TYPES.includes(e.type));
  const cachedMilCourse = !cachedEdu ? cachedExp?.jst?.military_courses?.[0] : null;
  const initialExperience = cachedEdu
    ? {
        ...EMPTY_EXPERIENCE,
        type: cachedEdu.type || '',
        field: cachedEdu.fields?.[0] || null,
        title: cachedEdu.description || '',
        skills: cachedEdu.skills || [],
        start_date: cachedEdu.start_date || null,
        end_date: cachedEdu.end_date || null,
        employer_provider: cachedEdu.employer_provider || '',
        comments: cachedEdu.comments || '',
        level: cachedEdu.level ?? null,
        is_current: cachedEdu.is_current ?? false,
      }
    : cachedMilCourse
    ? {
        ...EMPTY_EXPERIENCE,
        type: 'militaryCourse',
        title: cachedMilCourse.course_title || '',
        employer_provider: cachedMilCourse.institution_provider || '',
        start_date: cachedMilCourse.start_date || null,
        end_date: cachedMilCourse.end_date || null,
        skills: cachedMilCourse.parsed_skills || [],
        is_current: cachedMilCourse.currently_enrolled ?? false,
      }
    : EMPTY_EXPERIENCE;

  const putMilitaryCourses = useCallback(
    (nextCourses) => {
      const jst = cachedExp?.jst || {};
      return apiRequest('PUT', '/users/me/experience/', {
        data: { jst: { ...jst, military_courses: nextCourses } },
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ['/users/me/experience/'] });
      });
    },
    [cachedExp, queryClient]
  );

  const upsertMilitaryCourse = useCallback(
    (course, { courseId = null, index = -1 } = {}) => {
      const existing = cachedExp?.jst?.military_courses || [];
      let matchIdx = -1;
      if (courseId != null) {
        matchIdx = existing.findIndex((c) => c.military_course_id === courseId);
      }
      if (matchIdx < 0 && typeof index === 'number' && index >= 0 && index < existing.length) {
        matchIdx = index;
      }
      const nextCourses = matchIdx >= 0 ? existing.map((c, i) => (i === matchIdx ? course : c)) : [...existing, course];
      return putMilitaryCourses(nextCourses);
    },
    [cachedExp, putMilitaryCourses]
  );

  const [experiences, setExperiences] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAppendItem = (item) => {
    setExperiences([...experiences, item]);
    trackEvent('ONBOARDING_EDUCATION_EXPERIENCE_COMPLETE');
    // If the user previously saved a military course but then changed to a different
    // type, clear the orphaned JST military_courses entry before navigating.
    const hadMilitaryCourse = (cachedExp?.jst?.military_courses || []).length > 0;
    if (hadMilitaryCourse && item.type !== 'militaryCourse') {
      putMilitaryCourses([]).finally(() => {
        history.push(`${urlPrefix}/onboarding/location?from=education`);
      });
    } else {
      history.push(`${urlPrefix}/onboarding/location?from=education`);
    }
  };

  const handleBack = () => {
    history.push({
      pathname: `${urlPrefix}/onboarding/eligibility`,
      state: { step: 2 },
    });
  };

  return (
    <Box sx={{ boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.1)' }}>
      <div className={styles.loginFlow__form__onboarding}>
        <Box px={3}>
          <Typography align="center" variant="h1" gutterBottom sx={{ fontSize: 24, mt: 1 }}>
            {intl.formatMessage({ id: 'experience.form.addEducationExperience' })}
          </Typography>
        </Box>
        <Box sx={{ maxWidth: 222, marginLeft: 'auto', marginRight: 'auto', mt: 4, mb: 1 }}>
          <OnboardingSteps activeStep={3} />
        </Box>
      </div>
      <hr className={layoutStyles.separatorBold} />
      <Box className={styles.loadingScreen__cardsContainer}>
        <OnboardingContentWrapper>
          <ExperienceWalletForm
            experience={initialExperience}
            experiences={experiences}
            formScope="education"
            appendItem={handleAppendItem}
            formId="onboarding-education-experience-form"
            onSubmittingChange={setIsSubmitting}
            showMilitaryCourse={hasMilitaryService}
            saveMilitaryCourses={upsertMilitaryCourse}
          />
          <OnboardingNavigationButtons
            onBack={handleBack}
            loading={isSubmitting}
            onNext={() => {
              const formElement = document.getElementById('onboarding-education-experience-form');
              if (formElement) {
                formElement.requestSubmit();
              }
            }}
            backId="back-education-experience-button"
            nextId="next-education-experience-button"
          />
        </OnboardingContentWrapper>
      </Box>
    </Box>
  );
}
